/**
 * UserSearch 组件
 * 从 MemoryService 迁移，适配 Axiom 架构
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Search, X, User, Loader2 } from 'lucide-react'
import { authService } from '@/shared/services/auth'
import { useAuthStore } from '@/shared/store/authStore'

interface UserSearchResult {
  user_id: string
  email?: string
  name?: string
  session_count?: number
  last_active_at?: string
}

interface UserSearchProps {
  onUserSelect: (userId: string) => void
  currentUserId?: string
}

export const UserSearch = ({ onUserSelect, currentUserId }: UserSearchProps) => {
  const { user } = useAuthStore()
  const [keyword, setKeyword] = useState('')
  const [results, setResults] = useState<UserSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // 检查是否有搜索权限
  const canSearch = user?.role === 'developer' || user?.role === 'admin'

  // 搜索用户
  const searchUsers = useCallback(async (query: string) => {
    if (!query.trim() || !canSearch) {
      setResults([])
      return
    }

    setLoading(true)
    setError(null)
    try {
      // 使用 IAM API 搜索用户
      const response = await authService.searchUsers({
        search: query,
        page_size: 20,
      })
      
      // 转换IAM API响应格式到UserSearchResult格式
      // user.id 是用户资源的主键，传给 Memory API 时参数名叫 user_id
      const results: UserSearchResult[] = response.data.map((user) => ({
        user_id: user.id,  // 用户主键统一用 id
        email: user.email,
        name: user.name || user.display_name || user.nickname,
        // session_count 和 last_active_at 需要从Memory Admin API获取，这里先留空
        // TODO: 后续可以从Memory Admin API获取用户的会话统计信息
      }))
      
      setResults(results)
    } catch (err: unknown) {
      console.error('Search failed:', err)
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { status?: number } }
        if (axiosError.response?.status === 403) {
          setError('无权限搜索用户')
        } else {
          setError('搜索失败')
        }
      } else {
        setError('搜索失败')
      }
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [canSearch])

  // 防抖搜索
  const handleInputChange = (value: string) => {
    setKeyword(value)
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    if (value.trim()) {
      setShowDropdown(true)
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(value)
      }, 300)
    } else {
      setResults([])
      setShowDropdown(false)
    }
  }

  // 选择用户
  const handleSelectUser = (userId: string) => {
    console.log('[UserSearch] User selected:', userId)
    onUserSelect(userId)
    setKeyword('')
    setResults([])
    setShowDropdown(false)
  }

  // 清空输入
  const handleClear = () => {
    setKeyword('')
    setResults([])
    setShowDropdown(false)
    inputRef.current?.focus()
  }

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 格式化时间
  const formatLastActive = (dateStr?: string) => {
    if (!dateStr) return '未知'
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days === 0) return '今天'
    if (days === 1) return '昨天'
    if (days < 7) return `${days}天前`
    return date.toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' })
  }

  return (
    <div className="relative">
      {/* 搜索框 */}
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-secondary border border-border shadow-sm">
        <Search className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={keyword}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => keyword && setShowDropdown(true)}
          placeholder={canSearch ? "搜索用户ID或邮箱..." : "当前用户"}
          disabled={!canSearch}
          className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder-muted-foreground"
        />
        {keyword && (
          <button onClick={handleClear} className="p-0.5 rounded hover:bg-black/10">
            <X className="w-3 h-3 text-muted-foreground" />
          </button>
        )}
        {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
      </div>

      {/* 当前用户显示（非搜索模式） */}
      {!canSearch && currentUserId && (
        <div className="mt-2 px-3 py-2 rounded-xl text-sm bg-secondary border border-border shadow-sm">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-blue-500" />
            <span className="text-foreground">{currentUserId}</span>
          </div>
        </div>
      )}

      {/* 下拉结果 */}
      {showDropdown && canSearch && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-lg overflow-hidden z-50 max-h-64 overflow-y-auto bg-card border border-border"
        >
          {error ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {error}
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              {loading ? '搜索中...' : keyword ? '未找到匹配用户' : '输入关键词搜索'}
            </div>
          ) : (
            results.map((result) => (
              <button
                key={result.user_id}
                onClick={() => handleSelectUser(result.user_id)}
                className={`w-full text-left px-3 py-2 transition-colors hover:bg-secondary ${
                  result.user_id === currentUserId ? 'bg-blue-500/10' : ''
                }`}
              >
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 flex-shrink-0 text-blue-500" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-foreground">
                      {result.name || result.email || result.user_id}
                    </div>
                    <div className="text-xs flex items-center gap-2 text-muted-foreground">
                      {result.email && result.email !== result.user_id && (
                        <>
                          <span className="truncate">{result.email}</span>
                          <span>|</span>
                        </>
                      )}
                      <span className="truncate">{result.user_id}</span>
                      {result.session_count !== undefined && (
                        <>
                          <span>|</span>
                          <span>{result.session_count} 个会话</span>
                        </>
                      )}
                      {result.last_active_at && (
                        <>
                          <span>|</span>
                          <span>最近: {formatLastActive(result.last_active_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export default UserSearch

